import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { OrgLayout } from "../../components/Shell";
import { CopyButton, ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, Switch, fmtNum, useToast } from "../../components/ui";
import { ErrorState, Skeleton } from "../../components/States";
import { formatDateTime, formatRelative } from "../../lib/format";
import { DESTINATION_FIELDS, billingKeys, exportsApi, validateDestination, type Destination, type DestinationInput, type DestinationType, type ExportRun } from "./api";

const emptyInput = (type: DestinationType = "s3"): DestinationInput => ({ type, name: "", phiCovered: false, config: {}, secrets: {} });

export function ExportsPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const dests = useQuery({ queryKey: billingKeys.destinations(orgId), queryFn: () => exportsApi.destinations(orgId), retry: 0 });
  const runs = useQuery({ queryKey: billingKeys.runs(orgId), queryFn: () => exportsApi.runs(orgId), retry: 0, refetchInterval: (q) => (q.state.data?.some((r) => r.status === "queued" || r.status === "running") ? 5000 : false) });
  const [draft, setDraft] = useState<DestinationInput | null>(null);
  const [removing, setRemoving] = useState<Destination | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState<Record<string, string> | null>(null);

  const create = useMutation({
    mutationFn: (d: DestinationInput) => exportsApi.createDestination(orgId, d),
    onSuccess: (_d, input) => {
      // Secrets are shown once, here, and never returned by the API again.
      setRevealed(Object.fromEntries(Object.entries(input.secrets).filter(([, v]) => v)));
      setDraft(null);
      void qc.invalidateQueries({ queryKey: billingKeys.destinations(orgId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const remove = useMutation({
    mutationFn: (id: string) => exportsApi.deleteDestination(orgId, id),
    onSuccess: () => {
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: billingKeys.destinations(orgId) });
      toast.push("info", "Destination removed.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const run = useMutation({
    mutationFn: (destinationId?: string) => exportsApi.run(orgId, destinationId),
    onSuccess: () => {
      toast.push("info", "Export queued. Runs refresh every few seconds while in progress.");
      void qc.invalidateQueries({ queryKey: billingKeys.runs(orgId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const submit = () => {
    if (!draft) return;
    const e = validateDestination(draft);
    setErrors(e);
    if (Object.keys(e).length === 0) create.mutate(draft);
  };

  const runColumns = [
    { key: "when", header: "Started", label: "Started", render: (r: ExportRun) => (r.startedAt ? formatRelative(r.startedAt) : formatRelative(r.createdAt)) },
    { key: "trigger", header: "Trigger", label: "Trigger", render: (r: ExportRun) => r.trigger },
    { key: "status", header: "Status", label: "Status", render: (r: ExportRun) => <span className={`badge ${r.status === "done" ? "ok" : r.status === "failed" ? "danger" : "warn"}`}>{r.status}</span> },
    { key: "window", header: "Window", label: "Window", render: (r: ExportRun) => (r.windowFrom && r.windowTo ? `${formatDateTime(r.windowFrom)} → ${formatDateTime(r.windowTo)}` : "—") },
    { key: "rows", header: "Rows", label: "Rows", align: "right" as const, render: (r: ExportRun) => fmtNum(r.rows) },
    { key: "bytes", header: "Bytes", label: "Bytes", align: "right" as const, render: (r: ExportRun) => fmtBytes(r.bytes) },
    { key: "files", header: "Files", label: "Files", align: "right" as const, render: (r: ExportRun) => r.files },
    { key: "error", header: "", render: (r: ExportRun) => (r.error ? <span className="danger small" title={r.error}>{r.error.slice(0, 80)}</span> : null) },
  ];

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader
        title="Warehouse exports"
        actions={
          <div className="row">
            <button className="btn" onClick={() => run.mutate(undefined)} disabled={run.isPending || (dests.data?.length ?? 0) === 0}>Run now</button>
            <button className="btn primary" onClick={() => { setErrors({}); setDraft(emptyInput()); }}>Add destination</button>
          </div>
        }
      />

      <div className="card stack-table">
        {dests.isPending ? (
          <Skeleton />
        ) : dests.isError ? (
          <ErrorState error={dests.error} retry={() => void dests.refetch()} />
        ) : (
          <DataTable<Destination>
            ariaLabel="Export destinations"
            rows={dests.data}
            rowKey={(d) => d.id}
            empty={<span>No destinations yet. Add your warehouse to receive nightly exports.</span>}
            columns={[
              { key: "name", header: "Destination", label: "Destination", render: (d) => <div><strong>{d.name}</strong><div className="muted small">{DESTINATION_FIELDS[d.type].label}</div></div> },
              { key: "target", header: "Target", label: "Target", render: (d) => <span className="mono small">{targetOf(d)}</span> },
              { key: "phi", header: "PHI", label: "PHI", render: (d) => (d.phiCovered ? <span className="badge ok">covered</span> : <span className="badge neutral">ids &amp; counts</span>) },
              { key: "last", header: "Last run", label: "Last run", render: (d) => (d.lastRunAt ? <span title={d.lastError}>{formatRelative(d.lastRunAt)}{d.lastError ? " · failed" : ""}</span> : "never") },
              { key: "actions", header: "", render: (d) => (
                <RowMenu label={`Actions for ${d.name}`}>
                  <button type="button" role="menuitem" onClick={() => run.mutate(d.id)} disabled={run.isPending}>Run now</button>
                  <button type="button" role="menuitem" className="danger" onClick={() => setRemoving(d)}>Remove</button>
                </RowMenu>
              ) },
            ]}
          />
        )}
      </div>

      <div className="card stack-table" style={{ marginTop: 16 }}>
        <div className="section-head"><h2>Runs</h2></div>
        <DataTable<ExportRun> ariaLabel="Export runs" rows={runs.data ?? []} rowKey={(r) => r.id} loading={runs.isPending} empty={<span>No runs yet.</span>} columns={runColumns} />
      </div>

      <Dialog
        open={draft !== null}
        onClose={() => setDraft(null)}
        title="Add export destination"
        width={640}
        footer={
          <>
            <button className="btn" onClick={() => setDraft(null)}>Cancel</button>
            <button className="btn primary" onClick={submit} disabled={create.isPending}>Create</button>
          </>
        }
      >
        {draft && (
          <div className="stack">
            <Field label="Type">
              {(p) => (
                <select {...p} className="input" value={draft.type} onChange={(e) => setDraft(emptyInput(e.target.value as DestinationType))}>
                  {(Object.keys(DESTINATION_FIELDS) as DestinationType[]).map((t) => <option key={t} value={t}>{DESTINATION_FIELDS[t].label}</option>)}
                </select>
              )}
            </Field>
            <p className="muted small">{DESTINATION_FIELDS[draft.type].hint}</p>
            <Field label="Name" error={errors.name}>
              {(p) => <input {...p} className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />}
            </Field>
            {DESTINATION_FIELDS[draft.type].config.map((f) => (
              <Field key={f.key} label={f.label + (f.required ? "" : " (optional)")} error={errors[f.key]}>
                {(p) => <input {...p} className="input" placeholder={f.placeholder} value={String(draft.config[f.key] ?? "")} onChange={(e) => setDraft({ ...draft, config: { ...draft.config, [f.key]: e.target.value } })} />}
              </Field>
            ))}
            {DESTINATION_FIELDS[draft.type].secrets.map((s) => (
              <Field key={s.key} label={s.label + (s.required ? "" : " (optional)")} hint="Stored encrypted; shown once after saving." error={errors[s.key]}>
                {(p) =>
                  s.multiline ? (
                    <textarea {...p} className="input mono" rows={4} value={draft.secrets[s.key] ?? ""} onChange={(e) => setDraft({ ...draft, secrets: { ...draft.secrets, [s.key]: e.target.value } })} />
                  ) : (
                    <input {...p} className="input" type="password" autoComplete="off" value={draft.secrets[s.key] ?? ""} onChange={(e) => setDraft({ ...draft, secrets: { ...draft.secrets, [s.key]: e.target.value } })} />
                  )
                }
              </Field>
            ))}
            <Switch checked={draft.phiCovered} onChange={(v) => setDraft({ ...draft, phiCovered: v })} label="This destination is covered by our own BAA and controls" hint="Exports full text fields for PHI organizations." />
          </div>
        )}
      </Dialog>

      <Dialog open={revealed !== null} onClose={() => setRevealed(null)} title="Destination created" footer={<button className="btn primary" onClick={() => setRevealed(null)}>Done</button>}>
        <p>Secrets are encrypted at rest and will not be shown again. Copy anything you need now.</p>
        {revealed && Object.entries(revealed).map(([k, v]) => (
          <div key={k} className="secret-row">
            <span className="mono small">{k}</span>
            <code className="secret" data-testid={`secret-${k}`}>{v.length > 24 ? v.slice(0, 24) + "…" : v}</code>
            <CopyButton value={v} />
          </div>
        ))}
      </Dialog>
      <ConfirmDialog
        open={!!removing}
        danger
        title="Remove destination"
        confirmLabel="Remove destination"
        busy={remove.isPending}
        onClose={() => { if (!remove.isPending) setRemoving(null); }}
        onConfirm={() => removing && remove.mutate(removing.id)}
      >
        <p style={{ margin: 0 }}><strong>{removing?.name}</strong> stops receiving exports. Files already delivered stay where they are.</p>
      </ConfirmDialog>
    </OrgLayout>
  );
}

function targetOf(d: Destination): string {
  const c = d.config as Record<string, string | undefined>;
  switch (d.type) {
    case "s3":
      return `s3://${c.bucket ?? ""}/${c.prefix ?? ""}`;
    case "snowflake":
      return `${c.account ?? ""} · ${c.database ?? ""}.${c.schema ?? ""}`;
    case "bigquery":
      return `${c.project ?? ""}.${c.dataset ?? ""}`;
  }
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
